import { useMemo } from 'react';

interface UseSectionHighlightResult {
  /** Section ID to highlight (from URL hash in preview mode) */
  targetSectionId: string | null;
  /** Whether we're in preview mode */
  isPreviewMode: boolean;
}

/**
 * useSectionHighlight - Extracts target section ID from URL hash in preview mode.
 *
 * Single responsibility: Determine which section should be highlighted.
 */
export function useSectionHighlight(): UseSectionHighlightResult {
  const result = useMemo(() => {
    if (typeof window === 'undefined') {
      return { targetSectionId: null, isPreviewMode: false };
    }

    const isPreviewMode =
      new URLSearchParams(window.location.search).get('preview') === 'true';
    const hash = window.location.hash;

    if (isPreviewMode && hash) {
      return {
        targetSectionId: hash.slice(1), // Remove the '#'
        isPreviewMode: true,
      };
    }

    return { targetSectionId: null, isPreviewMode };
  }, []);

  return result;
}

/**
 * Adds highlight class to a specific section in HTML content.
 *
 * @param htmlContent - Raw HTML content
 * @param sectionId - ID of section to highlight
 * @param highlightClass - CSS class to add (default: 'activeHighlight')
 * @returns Processed HTML with highlight class added
 */
export function addHighlightToSection(
  htmlContent: string,
  sectionId: string | null,
  highlightClass = 'activeHighlight'
): string {
  if (!htmlContent || !sectionId) return htmlContent;

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    const el = doc.getElementById(sectionId);

    if (el) {
      el.classList.add(highlightClass);
      return doc.body.innerHTML;
    }
  } catch {
    // Return original content on parse error
  }

  return htmlContent;
}
